import React from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Container,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
  Chip,
  Avatar,
} from '@mui/material';
import Navbar from "./Navbar";
import {
  Group as GroupIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from "framer-motion";
import { keyframes } from "@mui/system";

// Custom CSS animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const glowAnimation = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(59, 130, 246, 0.5); }
`;

const shimmerAnimation = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function Lider() {
  const theme = useTheme();

  // Sections data with zigzag pattern
  const sections = [
    {
      title: "About Us",
      icon: "/images/logo_only.png",
      items: [
        "We provide innovative solutions tailored to your unique goals. Our dedicated team is committed to delivering measurable results.",
        "We provide a range of services designed to help startups and small businesses thrive.",
        "Founded in 2023, Startup Company has quickly become a leader in the startup and small business community.",
        "Our team of experienced professionals is dedicated to helping businesses of all sizes achieve their goals."
      ],
      color: "#3b82f6",
      gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
    },
    {
      title: "Our Purpose",
      icon: "/images/logo_only.png",
      items: [
        "Lider Technology is an IT Services Company based in India, specializing in Managed IT Infrastructure Services. We empower businesses with reliable, scalable and cost-effective technology solutions.",
        "The company is committed to providing a good work environment for its team and clients.",
        "It develops customized applications and websites tailored to user requirements. Our focus is on creating seamless, user-friendly experiences that drive engagement.",
        "By delivering personalized solutions, Lidertechnology adds significant value to its clients' business operations."
      ],
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981, #047857)",
    },
    {
      title: "Lider Technology Solutions",
      icon: "/images/logo_only.png",
      items: [
        "Lider Technology solutions company plays an crucial role in today's digital landscape. They drive innovation, streamline operations and help organizations stay competitive.",
        "Lider Technology Solutions distinguishes itself through innovation, efficacy and customer-centricity.",
        "The company is dedicated to providing customized strategies and services that address the specific needs of each client. Our client-centric approach ensures optimal outcomes and long-term success.",
        "Lider's operations are guided by a strong customer-centric approach, making it the foundation of the company's philosophy."
      ],
      color: "#8b5cf6",
      gradient: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
    },
    {
      title: "Our Company",
      icon: "/images/logo_only.png",
      items: [
        "The Enterprise Application Development Company in Chennai, India, delivering scalable and efficient digital solutions across industries.",
        "With a highly ingenious and futuristic development team, we develop and deploy industry-best, in-demand Android and iOS applications.",
        "We offer high-end services at sensible price tags, ensuring top-tier quality without compromising on cost-efficiency.",
        "Our applications are stable and secure, with advanced threat defense mechanisms incorporated, along with ongoing support to ensure long-term performance."
      ],
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
    },
    // {
    //   title: "Technologies Used Now",
    //   icon: "/images/technologies.jpg",
    //   items: [
    //     "Develop a strong brand identity and invest in marketing strategies to increase awareness and attract customers to your startup.",
    //     "Assemble a talented and diverse team with the skills and expertise required to execute your business plan effectively.",
    //     "Embrace cloud computing, data analytics, artificial intelligence (AI) and other emerging technologies to gain a competitive edge.",
    //     "Collaborate with other companies, industry associations and ecosystems to explore new markets, share resources and accelerate growth."
    //   ],
    //   color: "#06b6d4",
    //   gradient: "linear-gradient(135deg, #06b6d4, #0891b2)",
    // }
  ];

  // Statistics
  const statistics = [
    { value: "500+", label: "Clients", icon: "👥" },
    { value: "99%", label: "Satisfaction", icon: "⭐" },
    { value: "24/7", label: "Support", icon: "🛡️" },
    { value: "50+", label: "Projects", icon: "🚀" },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.34, 1.56, 0.64, 1]
      }
    },
    hover: {
      scale: 1.02,
      y: -10,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, x: -50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const reverseImageVariants = {
    hidden: { opacity: 0, scale: 0.8, x: 50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const reverseContentVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (custom) => ({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        delay: custom * 0.1,
        ease: "easeOut"
      }
    }),
    hover: {
      x: 10,
      transition: { duration: 0.2 }
    }
  };

  // Floating particles background
  const FloatingParticles = ({ count = 15 }) => (
    <Box sx={{
      position: 'absolute',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'absolute',
            width: Math.random() * 10 + 2,
            height: Math.random() * 10 + 2,
            background: `rgba(59, 130, 246, ${Math.random() * 0.1})`,
            borderRadius: '50%',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            x: i % 2 === 0 ? [0, 20, 0] : [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5,
          }}
        />
      ))}
    </Box>
  );

  // Interactive gradient background
  const GradientBackground = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 30%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(6, 182, 212, 0.05) 0%, transparent 50%),
          linear-gradient(135deg, #f0f9ff 0%, #f8fafc 50%, #f1f5f9 100%)
        `,
        backgroundSize: '200% 200%',
        animation: `${gradientShift} 15s ease infinite`,
        zIndex: -1,
      }}
    />
  );

  return (
    <Box sx={{ 
      backgroundColor: "#f8fafc", 
      color: "#1e293b", 
      overflow: "hidden",
      minHeight: "100vh",
      position: "relative"
    }}>
      <GradientBackground />
      <FloatingParticles />

      {/* Navbar */}
      <Navbar />

      {/* Main Content Sections */}
      <Box sx={{ py: 8, position: 'relative' }}>
        <Container maxWidth="xl">
          {sections.map((section, index) => (
            <Box key={index} sx={{ my: 8 }}>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <motion.div variants={itemVariants}>
                  <motion.div
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    whileHover="hover"
                  >
                    <Card
                      sx={{
                        background: "white",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(0, 0, 0, 0.05)",
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: "0 10px 30px rgba(59,130,246,0.1)",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        '&:hover': {
                          borderColor: section.color,
                          boxShadow: `0 25px 60px ${alpha(section.color, 0.15)}`,
                          '&:before': {
                            transform: 'translateX(0%)',
                          }
                        },
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: section.gradient,
                          transform: 'translateX(-100%)',
                          transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                          zIndex: 1,
                        }
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Grid container spacing={4} alignItems="center">
                          {/* Left Image for even indexes */}
                          {index % 2 === 0 ? (
                            <>
                              {/* Image on Left */}
                              <Grid item xs={12} md={5}>
                                <motion.div
                                  variants={imageVariants}
                                  initial="hidden"
                                  whileInView="visible"
                                  viewport={{ once: true }}
                                >
                                  <Box sx={{ 
                                    textAlign: "center",
                                    position: 'relative',
                                  }}>
                                    <motion.div
                                      whileHover={{ 
                                        scale: 1.05,
                                        rotate: [0, 5, -5, 0],
                                        transition: { duration: 0.5 }
                                      }}
                                    >
                                      <Avatar
                                        src={section.icon}
                                        alt={section.title}
                                        sx={{ 
                                          width: 120,
                                          height: 120,
                                          margin: "0 auto 20px auto",
                                          border: `4px solid ${alpha(section.color, 0.2)}`,
                                          boxShadow: `0 6px 12px ${alpha(section.color, 0.2)}`,
                                          background: section.gradient,
                                          color: "white"
                                        }}
                                      >
                                        {section.title.charAt(0)}
                                      </Avatar>
                                    </motion.div>
                                    <motion.div
                                      initial={{ opacity: 0, y: 20 }}
                                      whileInView={{ 
                                        opacity: 1, 
                                        y: 0,
                                        transition: { duration: 0.6, delay: 0.2 }
                                      }}
                                      viewport={{ once: true }}
                                    >
                                      <Typography
                                        variant="h4"
                                        component="h3"
                                        sx={{
                                          fontWeight: "800",
                                          fontSize: { xs: "1.8rem", md: "2.2rem" },
                                          background: section.gradient,
                                          WebkitBackgroundClip: "text",
                                          WebkitTextFillColor: "transparent",
                                          backgroundClip: "text",
                                          position: 'relative',
                                          display: 'inline-block',
                                          '&:after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -8,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '0%',
                                            height: '4px',
                                            background: section.gradient,
                                            transition: 'width 0.6s ease',
                                          },
                                          '&:hover:after': {
                                            width: '80%',
                                          }
                                        }}
                                      >
                                        {section.title}
                                      </Typography>
                                    </motion.div>
                                  </Box>
                                </motion.div>
                              </Grid>

                              {/* Content on Right */}
                              <Grid item xs={12} md={7}>
                                <motion.div
                                  variants={contentVariants}
                                  initial="hidden"
                                  whileInView="visible"
                                  viewport={{ once: true }}
                                >
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                      <List>
                                        {[0, 1].map((itemIndex) => (
                                          <motion.div
                                            key={itemIndex}
                                            custom={itemIndex}
                                            variants={listItemVariants}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover="hover"
                                          >
                                            <ListItem sx={{ alignItems: "flex-start", py: 2.5, px: 0 }}>
                                              <motion.div
                                                whileHover={{ 
                                                  scale: 1.3,
                                                  rotate: 10,
                                                  transition: { duration: 0.2 }
                                                }}
                                              >
                                                <Box sx={{ minWidth: 45, display: "flex", justifyContent: "center", pt: "4px" }}>
                                                  <CheckCircleIcon sx={{ 
                                                    color: section.color, 
                                                    fontSize: "32px",
                                                    filter: `drop-shadow(0 2px 4px ${alpha(section.color, 0.2)})`
                                                  }} />
                                                </Box>
                                              </motion.div>
                                              <ListItemText
                                                primary={section.items[itemIndex]}
                                                primaryTypographyProps={{
                                                  fontWeight: "500",
                                                  fontSize: { xs: "16px", md: "18px" },
                                                  lineHeight: 1.7,
                                                  color: "#475569",
                                                }}
                                                sx={{ 
                                                  ml: 2,
                                                  '&:hover .MuiListItemText-primary': {
                                                    color: section.color,
                                                    transition: 'color 0.3s ease'
                                                  }
                                                }}
                                              />
                                            </ListItem>
                                          </motion.div>
                                        ))}
                                      </List>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <List>
                                        {[2, 3].map((itemIndex) => (
                                          <motion.div
                                            key={itemIndex}
                                            custom={itemIndex}
                                            variants={listItemVariants}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover="hover"
                                          >
                                            <ListItem sx={{ alignItems: "flex-start", py: 2.5, px: 0 }}>
                                              <motion.div
                                                whileHover={{ 
                                                  scale: 1.3,
                                                  rotate: 10,
                                                  transition: { duration: 0.2 }
                                                }}
                                              >
                                                <Box sx={{ minWidth: 45, display: "flex", justifyContent: "center", pt: "4px" }}>
                                                  <CheckCircleIcon sx={{ 
                                                    color: section.color, 
                                                    fontSize: "32px",
                                                    filter: `drop-shadow(0 2px 4px ${alpha(section.color, 0.2)})`
                                                  }} />
                                                </Box>
                                              </motion.div>
                                              <ListItemText
                                                primary={section.items[itemIndex]}
                                                primaryTypographyProps={{
                                                  fontWeight: "500",
                                                  fontSize: { xs: "16px", md: "18px" },
                                                  lineHeight: 1.7,
                                                  color: "#475569",
                                                }}
                                                sx={{ 
                                                  ml: 2,
                                                  '&:hover .MuiListItemText-primary': {
                                                    color: section.color,
                                                    transition: 'color 0.3s ease'
                                                  }
                                                }}
                                              />
                                            </ListItem>
                                          </motion.div>
                                        ))}
                                      </List>
                                    </Grid>
                                  </Grid>
                                </motion.div>
                              </Grid>
                            </>
                          ) : (
                            <>
                              {/* Content on Left for odd indexes */}
                              <Grid item xs={12} md={7}>
                                <motion.div
                                  variants={reverseContentVariants}
                                  initial="hidden"
                                  whileInView="visible"
                                  viewport={{ once: true }}
                                >
                                  <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                      <List>
                                        {[0, 1].map((itemIndex) => (
                                          <motion.div
                                            key={itemIndex}
                                            custom={itemIndex}
                                            variants={listItemVariants}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover="hover"
                                          >
                                            <ListItem sx={{ alignItems: "flex-start", py: 2.5, px: 0 }}>
                                              <motion.div
                                                whileHover={{ 
                                                  scale: 1.3,
                                                  rotate: 10,
                                                  transition: { duration: 0.2 }
                                                }}
                                              >
                                                <Box sx={{ minWidth: 45, display: "flex", justifyContent: "center", pt: "4px" }}>
                                                  <CheckCircleIcon sx={{ 
                                                    color: section.color, 
                                                    fontSize: "32px",
                                                    filter: `drop-shadow(0 2px 4px ${alpha(section.color, 0.2)})`
                                                  }} />
                                                </Box>
                                              </motion.div>
                                              <ListItemText
                                                primary={section.items[itemIndex]}
                                                primaryTypographyProps={{
                                                  fontWeight: "500",
                                                  fontSize: { xs: "16px", md: "18px" },
                                                  lineHeight: 1.7,
                                                  color: "#475569",
                                                }}
                                                sx={{ 
                                                  ml: 2,
                                                  '&:hover .MuiListItemText-primary': {
                                                    color: section.color,
                                                    transition: 'color 0.3s ease'
                                                  }
                                                }}
                                              />
                                            </ListItem>
                                          </motion.div>
                                        ))}
                                      </List>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                      <List>
                                        {[2, 3].map((itemIndex) => (
                                          <motion.div
                                            key={itemIndex}
                                            custom={itemIndex}
                                            variants={listItemVariants}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover="hover"
                                          >
                                            <ListItem sx={{ alignItems: "flex-start", py: 2.5, px: 0 }}>
                                              <motion.div
                                                whileHover={{ 
                                                  scale: 1.3,
                                                  rotate: 10,
                                                  transition: { duration: 0.2 }
                                                }}
                                              >
                                                <Box sx={{ minWidth: 45, display: "flex", justifyContent: "center", pt: "4px" }}>
                                                  <CheckCircleIcon sx={{ 
                                                    color: section.color, 
                                                    fontSize: "32px",
                                                    filter: `drop-shadow(0 2px 4px ${alpha(section.color, 0.2)})`
                                                  }} />
                                                </Box>
                                              </motion.div>
                                              <ListItemText
                                                primary={section.items[itemIndex]}
                                                primaryTypographyProps={{
                                                  fontWeight: "500",
                                                  fontSize: { xs: "16px", md: "18px" },
                                                  lineHeight: 1.7,
                                                  color: "#475569",
                                                }}
                                                sx={{ 
                                                  ml: 2,
                                                  '&:hover .MuiListItemText-primary': {
                                                    color: section.color,
                                                    transition: 'color 0.3s ease'
                                                  }
                                                }}
                                              />
                                            </ListItem>
                                          </motion.div>
                                        ))}
                                      </List>
                                    </Grid>
                                  </Grid>
                                </motion.div>
                              </Grid>

                              {/* Image on Right for odd indexes */}
                              <Grid item xs={12} md={5}>
                                <motion.div
                                  variants={reverseImageVariants}
                                  initial="hidden"
                                  whileInView="visible"
                                  viewport={{ once: true }}
                                >
                                  <Box sx={{ 
                                    textAlign: "center",
                                    position: 'relative',
                                  }}>
                                    <motion.div
                                      whileHover={{ 
                                        scale: 1.05,
                                        rotate: [0, 5, -5, 0],
                                        transition: { duration: 0.5 }
                                      }}
                                    >
                                      <Avatar
                                        src={section.icon}
                                        alt={section.title}
                                        sx={{ 
                                          width: 120,
                                          height: 120,
                                          margin: "0 auto 20px auto",
                                          border: `4px solid ${alpha(section.color, 0.2)}`,
                                          boxShadow: `0 6px 12px ${alpha(section.color, 0.2)}`,
                                          background: section.gradient,
                                          color: "white"
                                        }}
                                      >
                                        {section.title.charAt(0)}
                                      </Avatar>
                                    </motion.div>
                                    <motion.div
                                      initial={{ opacity: 0, y: 20 }}
                                      whileInView={{ 
                                        opacity: 1, 
                                        y: 0,
                                        transition: { duration: 0.6, delay: 0.2 }
                                      }}
                                      viewport={{ once: true }}
                                    >
                                      <Typography
                                        variant="h4"
                                        component="h3"
                                        sx={{
                                          fontWeight: "800",
                                          fontSize: { xs: "1.8rem", md: "2.2rem" },
                                          background: section.gradient,
                                          WebkitBackgroundClip: "text",
                                          WebkitTextFillColor: "transparent",
                                          backgroundClip: "text",
                                          position: 'relative',
                                          display: 'inline-block',
                                          '&:after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -8,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '0%',
                                            height: '4px',
                                            background: section.gradient,
                                            transition: 'width 0.6s ease',
                                          },
                                          '&:hover:after': {
                                            width: '80%',
                                          }
                                        }}
                                      >
                                        {section.title}
                                      </Typography>
                                    </motion.div>
                                  </Box>
                                </motion.div>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              </motion.div>
            </Box>
          ))}
        </Container>
      </Box>

     
    </Box>
  );
}

export default Lider;
